import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PORTAL, usePortal } from '../../portalAuth';
import { STORE_BASE } from '../../routeBase';

const lbl = { fontSize: 12.5, fontWeight: 600, color: 'var(--sf-primary)', display: 'block', marginBottom: 5 };
const inp = { width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
const card = { background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 24 };

const STATUS_STYLE = {
  overdue: { bg: '#fef2f2', fg: '#b91c1c', label: 'Overdue' },
  today: { bg: '#fffbeb', fg: '#b45309', label: 'Due today' },
  upcoming: { bg: '#f0fdf4', fg: '#16a34a', label: 'Coming up' },
};

function recallMessage(name, years) {
  const first = (name || '').trim().split(' ')[0] || 'there';
  const span = years === 1 ? 'a year' : `${years} years`;
  return `Hi ${first}, it's been ${span} since you picked up your last pair of glasses with us! Just a quick reminder to check if you need an updated eye test or want to see our new collections.`;
}
function waLink(phone, text) {
  const digits = String(phone || '').replace(/\D/g, '');
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}
function fmtDate(d) { return d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'; }

const emptyForm = { id: null, name: '', phone: '', last_purchase_date: '', notes: '' };

export default function PatientRecall() {
  const { customer, ready } = usePortal();
  const [contacts, setContacts] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => PORTAL.get('/recall-contacts').then(r => setContacts(r.data.contacts || []));
  useEffect(() => { if (customer) load(); }, [customer]); // eslint-disable-line

  const due = useMemo(() => (contacts || []).filter(c => c.recall), [contacts]);
  const filtered = useMemo(() => {
    if (!contacts) return [];
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(c => c.name.toLowerCase().includes(q) || (c.phone || '').includes(q));
  }, [contacts, search]);

  const openAdd = () => { setForm(emptyForm); setShowForm(true); };
  const openEdit = (c) => { setForm({ id: c.id, name: c.name, phone: c.phone || '', last_purchase_date: c.last_purchase_date || '', notes: c.notes || '' }); setShowForm(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      if (form.id) await PORTAL.put(`/recall-contacts/${form.id}`, form);
      else await PORTAL.post('/recall-contacts', form);
      toast.success('Saved');
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this patient from your recall list?')) return;
    try { await PORTAL.delete(`/recall-contacts/${id}`); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not delete'); }
  };

  if (!ready) return <div style={{ padding: 60, textAlign: 'center', color: '#aaa' }}>Loading…</div>;
  if (!customer) return (
    <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center' }}>
      <h2>Please sign in to use Patient Recall</h2>
      <Link to={`${STORE_BASE}/login`} style={{ color: 'var(--sf-accent)' }}>Sign in</Link>
    </div>
  );

  return (
    <div style={{ background: 'var(--sf-body-bg)' }}>
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 60px' }}>
        <Link to={`${STORE_BASE}/tools`} style={{ fontSize: 12.5, color: 'var(--sf-hover)', fontWeight: 600, textDecoration: 'none' }}>
          <i className="ti ti-arrow-left" style={{ fontSize: 13, verticalAlign: -1 }} /> All free tools
        </Link>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, margin: '14px 0 6px' }}>
          <div>
            <h1 style={{ fontSize: 'clamp(22px,3.2vw,30px)', fontWeight: 800, color: 'var(--sf-primary)', margin: '0 0 6px' }}>Patient Recall</h1>
            <p style={{ fontSize: 13.5, color: 'var(--sf-muted)', margin: 0, maxWidth: 560 }}>
              A private list of your own patients — never shared outside your account. We flag anyone whose
              1-year or 2-year purchase anniversary is coming up, so you never miss the reminder.
            </p>
          </div>
          <button onClick={openAdd} style={{ background: 'var(--sf-primary)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
            + Add Patient
          </button>
        </div>

        {/* DAILY CONTACT LIST */}
        <div style={{ marginTop: 24, marginBottom: 28 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 12px' }}>
            Daily Contact List {due.length > 0 && <span style={{ color: 'var(--sf-hover)' }}>({due.length})</span>}
          </h3>
          {contacts === null && <div style={{ fontSize: 13, color: 'var(--sf-muted)' }}>Loading…</div>}
          {contacts !== null && due.length === 0 && (
            <div style={{ ...card, fontSize: 13, color: 'var(--sf-muted)' }}>No patients due for a recall reminder right now.</div>
          )}
          {due.length > 0 && (
            <div style={{ display: 'grid', gap: 10 }}>
              {due.map(c => {
                const s = STATUS_STYLE[c.recall.status];
                return (
                  <div key={c.id} style={{ ...card, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <span style={{ background: s.bg, color: s.fg, fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '4px 12px', flexShrink: 0 }}>
                      {s.label}
                    </span>
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{ fontWeight: 700, color: 'var(--sf-primary)', fontSize: 14 }}>{c.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--sf-muted)' }}>
                        {c.recall.years === 1 ? '1-year' : '2-year'} anniversary · {fmtDate(c.recall.anniversary_date)} {c.phone && `· ${c.phone}`}
                      </div>
                    </div>
                    {c.phone && (
                      <a href={waLink(c.phone, recallMessage(c.name, c.recall.years))} target="_blank" rel="noreferrer"
                        style={{ background: '#25D366', color: '#fff', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, textDecoration: 'none', flexShrink: 0 }}>
                        <i className="ti ti-brand-whatsapp" style={{ fontSize: 14, verticalAlign: -2, marginRight: 4 }} /> Send Message
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ALL PATIENTS */}
        <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 12px' }}>All Patients</h3>
        <input type="text" placeholder="Search by name or phone…" value={search} onChange={e => setSearch(e.target.value)} style={{ ...inp, maxWidth: 320, marginBottom: 14 }} />
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          {filtered.length === 0 && <div style={{ padding: 20, fontSize: 13, color: 'var(--sf-muted)' }}>No patients yet — add your first one above.</div>}
          {filtered.map((c, i) => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 18px', borderTop: i === 0 ? 'none' : '1px solid #f0f0f0', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontWeight: 600, color: 'var(--sf-primary)', fontSize: 13.5 }}>{c.name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--sf-muted)' }}>
                  {c.phone || 'No phone'} · Last purchase {fmtDate(c.last_purchase_date)}
                </div>
              </div>
              <button onClick={() => openEdit(c)} style={{ border: '1px solid #e5e5e5', background: '#fff', color: 'var(--sf-primary)', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>Edit</button>
              <button onClick={() => remove(c.id)} style={{ border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>Remove</button>
            </div>
          ))}
        </div>
      </section>

      {showForm && (
        <div onClick={() => setShowForm(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ ...card, width: '100%', maxWidth: 420 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 16px' }}>{form.id ? 'Edit Patient' : 'Add Patient'}</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Name</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inp} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Phone (with country code, digits only)</label>
              <input type="text" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="e.g. 919876543210" style={inp} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Last Purchase Date</label>
              <input type="date" value={form.last_purchase_date} onChange={e => setForm(f => ({ ...f, last_purchase_date: e.target.value }))} style={inp} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={lbl}>Notes (optional)</label>
              <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={inp} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{ border: '1px solid #e5e5e5', background: '#fff', color: 'var(--sf-primary)', borderRadius: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ border: 'none', background: 'var(--sf-primary)', color: '#fff', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
